import { useState, useEffect } from 'react';

// Unified binary media fetcher for all media types
// Ensures proper blob fetching without base64 conversion

/**
 * Fetch media as binary blob with proper error handling
 * @param {string} url - Media URL
 * @param {string} mimeType - Expected MIME type
 * @returns {Promise<{blob: Blob, url: string, size: number}>}
 */
export const fetchMediaAsBlob = async (url, mimeType = '*/*') => {
  try {
    console.log(`Fetching media as binary blob from: ${url}`);

    // If it's already a blob URL, return it directly WITHOUT re-fetching
    // Re-fetching blob URLs can corrupt video/audio playback
    if (url.startsWith('blob:')) {
      console.log('Using existing blob URL directly (no re-fetch):', url);
      
      // DON'T try to fetch the blob URL again - this can cause video playback issues
      // Just return the URL as-is with size 0 (size will be determined when media loads)
      return {
        blob: null, // Can't access the blob directly from blob URL
        url: url,
        size: 0 // Size will be determined by the media element itself
      };
    }

    // If it's a data URL, we need to handle it differently
    if (url.startsWith('data:')) {
      console.warn('Data URL detected - converting to blob URL:', url.substring(0, 50) + '...');
      
      try {
        // Extract base64 data from data URL
        const base64Data = url.split(',')[1];
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);
        
        console.log('Converted data URL to blob URL:', {
          originalSize: binaryData.length,
          blobSize: blob.size,
          type: blob.type
        });

        return {
          blob: blob,
          url: objectUrl,
          size: blob.size
        };
      } catch (error) {
        console.error('Failed to convert data URL to blob:', error);
        throw new Error(`Failed to process data URL: ${error.message}`);
      }
    }

    // For regular URLs, fetch as COMPLETE binary blob - NO partial loading
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': mimeType
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Get the COMPLETE blob - this ensures all metadata is loaded
    const blob = await response.blob();

    console.log('Media blob created successfully:', {
      size: blob.size,
      type: blob.type,
      url: url
    });

    // Create object URL for browser-safe media playback
    const objectUrl = URL.createObjectURL(blob);
    console.log('Object URL created for media:', objectUrl);

    return {
      blob,
      url: objectUrl,
      size: blob.size
    };

  } catch (error) {
    console.error('Error fetching media as blob:', error);
    throw new Error(`Failed to load media: ${error.message}`);
  }
};

/**
 * Clean up object URLs to prevent memory leaks
 * @param {string} url - Object URL to revoke
 */
export const revokeObjectUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
    console.log('Object URL revoked');
  }
};

/**
 * Hook for fetching media as binary blob with React state management
 * @param {string} mediaUrl - Media URL
 * @param {string} mimeType - Expected MIME type
 * @returns {object} - Media state and utilities
 */
export const useBinaryMedia = (mediaUrl, mimeType = '*/*') => {
  const [mediaState, setMediaState] = useState({
    url: null,
    blobSize: 0,
    isLoading: true,
    error: null,
    errorDetails: '',
    isExternalBlobUrl: false // Track if URL was passed in as blob URL
  });

  useEffect(() => {
    if (!mediaUrl) {
      setMediaState({
        url: null,
        blobSize: 0,
        isLoading: false,
        error: null,
        errorDetails: '',
        isExternalBlobUrl: false
      });
      return;
    }

    // Track if this is an external blob URL (should not be revoked by us)
    const isExternalBlob = mediaUrl.startsWith('blob:');

    // Track the URL we create locally so cleanup revokes the REAL url
    // (reading mediaState in cleanup would see a stale value and leak blobs)
    let createdUrl = null;
    let cancelled = false;

    const fetchMedia = async () => {
      try {
        setMediaState(prev => ({ ...prev, isLoading: true, error: null, errorDetails: '' }));

        const { url, size } = await fetchMediaAsBlob(mediaUrl, mimeType);

        if (cancelled) {
          // Component unmounted (or url changed) mid-fetch — free the blob now
          if (url && !isExternalBlob) revokeObjectUrl(url);
          return;
        }
        if (!isExternalBlob) createdUrl = url;

        setMediaState({
          url,
          blobSize: size,
          isLoading: false,
          error: null,
          errorDetails: '',
          isExternalBlobUrl: isExternalBlob
        });

      } catch (error) {
        if (cancelled) return;
        console.error('Error in useBinaryMedia:', error);
        setMediaState({
          url: null,
          blobSize: 0,
          isLoading: false,
          error: true,
          errorDetails: error.message,
          isExternalBlobUrl: false
        });
      }
    };

    fetchMedia();

    // Cleanup: revoke only URLs we created; external blob URLs belong to the API layer
    return () => {
      cancelled = true;
      if (createdUrl) revokeObjectUrl(createdUrl);
    };
  }, [mediaUrl, mimeType]);

  return mediaState;
};

// Re-export for convenience
const binaryMediaFetcher = {
  fetchMediaAsBlob,
  revokeObjectUrl,
  useBinaryMedia
};

export default binaryMediaFetcher;
